import { Ref, ref } from 'vue'

import { PolicyType } from '@/domain/value_objects/config/policyType'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import { NotificationViewModel } from '@/presentation/viewmodels/notification/notificationViewModel'
import { store } from '@/store'

export class UserRegistrationStatePresenter {
  id = ref('')
  password = ref('')
  confirmPassword = ref('')
  name = ref('')
  affiliation: Ref<string | null> = ref(null)
  countryIndex = ref(91)
  displayLanguageIndex = ref(0)
  defaultProgrammingLanguageIndex = ref(1)
  birthYear: Ref<number | null> = ref(null)
  avatar: Ref<string | null> = ref(null)
  mail: Ref<string | null> = ref(null)
  url: Ref<string | null> = ref(null)
  policyIndex = ref(0)
  private notificationViewModel: NotificationViewModel

  constructor(notificationViewModel: NotificationViewModel = new NotificationViewModel(store)) {
    this.notificationViewModel = notificationViewModel
  }

  setId = (id: string) => {
    this.id.value = id
  }

  setPassword = (password: string) => {
    this.password.value = password
  }

  setConfirmPassword = (confirmPassword: string) => {
    this.confirmPassword.value = confirmPassword
  }

  setName = (name: string) => {
    this.name.value = name
  }

  setAffiliation = (affiliation: string) => {
    this.affiliation.value = affiliation
  }

  setCountryIndex = (countryIndex: number) => {
    this.countryIndex.value = countryIndex
  }

  setDisplayLanguageIndex = (displayLanguageIndex: number) => {
    this.displayLanguageIndex.value = displayLanguageIndex
  }

  setDefaultProgrammingLanguageIndex = (defaultProgrammingLanguageIndex: number) => {
    this.defaultProgrammingLanguageIndex.value = defaultProgrammingLanguageIndex
  }

  setBirthYear = (birthYear: number) => {
    this.birthYear.value = birthYear
  }

  setAvatar = (avatar: string | null) => {
    this.avatar.value = avatar
  }

  setMail = (mail: string) => {
    this.mail.value = mail
  }

  setUrl = (url: string) => {
    this.url.value = url
  }

  setPolicyIndex = (policyIndex: number) => {
    this.policyIndex.value = policyIndex
  }

  checkPasswordCorrect = () => {
    return this.password.value === this.confirmPassword.value
  }

  uploadAvatar = (event: InputEvent) => {
    if (!event.target) return
    const target = event.target as HTMLInputElement
    const file = target.files && target.files[0]
    if (!file) return

    const reader = new FileReader()

    reader.onload = e => {
      if (!e.target) return
      const result = e.target.result
      if (typeof result === 'string') {
        this.avatar.value = result
      }
    }

    // ファイルをbase64形式に変換
    reader.readAsDataURL(file)
  }

  isAsciiOnly = (str: string): boolean => {
    // 半角アルファベット、半角数字、半角記号、空白のみ許可
    const asciiRegex = /^[\x20-\x7E]+$/
    return asciiRegex.test(str)
  }

  isNonEmptyAsciiOnly = (str: string): boolean => {
    // 半角アルファベット、半角数字、半角記号のみ許可
    const asciiRegex = /^[\x21-\x7E]+$/
    return asciiRegex.test(str)
  }

  isCorrectPasswordString = (str: string): boolean => {
    // 半角アルファベット、半角数字、半角記号のみで、4文字以上
    const asciiRegex = /^[\x21-\x7E]{4,}$/
    return asciiRegex.test(str)
  }

  checkValues = (displayLanguage: DisplayLanguageType): boolean => {
    if (this.id.value === '') {
      this.notificationViewModel.error(
        displayLanguage == 'Japanese' ? 'アカウントIDを入力してください' : 'Please enter your account ID'
      )
      return false
    }
    if (this.password.value === '') {
      this.notificationViewModel.error(
        displayLanguage == 'Japanese' ? 'パスワードを入力してください' : 'Please enter your password'
      )
      return false
    }
    if (this.name.value === '') {
      this.notificationViewModel.error(
        displayLanguage == 'Japanese' ? '名前を入力してください' : 'Please enter your name'
      )
      return false
    }
    if (!this.checkPasswordCorrect()) {
      this.notificationViewModel.error(
        displayLanguage == 'Japanese' ? 'パスワードが一致しません' : 'Passwords do not match'
      )
      return false
    }
    if (!this.isNonEmptyAsciiOnly(this.id.value)) {
      this.notificationViewModel.error(
        displayLanguage == 'Japanese' ? 'アカウントIDが無効です' : 'Account ID is invalid'
      )
      return false
    }
    if (!this.isCorrectPasswordString(this.password.value)) {
      this.notificationViewModel.error(displayLanguage == 'Japanese' ? 'パスワードが無効です' : 'Password is invalid')
      return false
    }
    if (!this.isAsciiOnly(this.name.value)) {
      this.notificationViewModel.error(displayLanguage == 'Japanese' ? '名前が無効です' : 'Name is invalid')
      return false
    }
    if (this.affiliation.value != null && !this.isAsciiOnly(this.affiliation.value)) {
      this.notificationViewModel.error(displayLanguage == 'Japanese' ? '所属が無効です' : 'Affiliation is invalid')
      return false
    }
    return true
  }

  get policyList(): PolicyType[] {
    return ['public', 'private']
  }

  get displayLanguageList(): DisplayLanguageType[] {
    return ['Japanese', 'English']
  }

  get programmingLanguageList(): ProgramLanguageType[] {
    return [
      'C',
      'C++',
      'C++11',
      'C++14',
      'C++17',
      'C++20',
      'C++23',
      'C#',
      'D',
      'JAVA',
      'Python',
      'Python3',
      'PyPy3',
      'PHP',
      'JavaScript',
      'Ruby',
      'Rust',
      'Kotlin',
      'Go',
      'Scala',
      'Haskell',
      'OCaml'
    ]
  }

  get countryList(): { value: string; label: string }[] {
    return [
      {
        value: 'af',
        label: 'Afghanistan'
      },
      {
        value: 'al',
        label: 'Albania'
      },
      {
        value: 'dz',
        label: 'Algeria'
      },
      {
        value: 'ad',
        label: 'Andorra'
      },
      {
        value: 'ao',
        label: 'Angola'
      },
      {
        value: 'ag',
        label: 'Antigua and Barbuda'
      },
      {
        value: 'ar',
        label: 'Argentina'
      },
      {
        value: 'am',
        label: 'Armenia'
      },
      {
        value: 'aw',
        label: 'Aruba'
      },
      {
        value: 'au',
        label: 'Australia'
      },
      {
        value: 'at',
        label: 'Austria'
      },
      {
        value: 'az',
        label: 'Azerbaijan'
      },
      {
        value: 'bs',
        label: 'Bahamas'
      },
      {
        value: 'bh',
        label: 'Bahrain'
      },
      {
        value: 'bd',
        label: 'Bangladesh'
      },
      {
        value: 'bb',
        label: 'Barbados'
      },
      {
        value: 'by',
        label: 'Belarus'
      },
      {
        value: 'be',
        label: 'Belgium'
      },
      {
        value: 'bz',
        label: 'Belize'
      },
      {
        value: 'bj',
        label: 'Benin'
      },
      {
        value: 'bm',
        label: 'Bermuda'
      },
      {
        value: 'bt',
        label: 'Bhutan'
      },
      {
        value: 'bo',
        label: 'Bolivia'
      },
      {
        value: 'ba',
        label: 'Bosnia and Herzegovina'
      },
      {
        value: 'bw',
        label: 'Botswana'
      },
      {
        value: 'br',
        label: 'Brazil'
      },
      {
        value: 'bn',
        label: 'Brunei'
      },
      {
        value: 'bg',
        label: 'Bulgaria'
      },
      {
        value: 'bf',
        label: 'Burkina Faso'
      },
      {
        value: 'bi',
        label: 'Burundi'
      },
      {
        value: 'cv',
        label: 'Cabo Verde'
      },
      {
        value: 'kh',
        label: 'Cambodia'
      },
      {
        value: 'cm',
        label: 'Cameroon'
      },
      {
        value: 'ca',
        label: 'Canada'
      },
      {
        value: 'ky',
        label: 'Cayman Islands'
      },
      {
        value: 'cf',
        label: 'Central African Republic'
      },
      {
        value: 'td',
        label: 'Chad'
      },
      {
        value: 'cl',
        label: 'Chile'
      },
      {
        value: 'cn',
        label: 'China'
      },
      {
        value: 'co',
        label: 'Colombia'
      },
      {
        value: 'km',
        label: 'Comoros'
      },
      {
        value: 'cg',
        label: 'Republic of the Congo'
      },
      {
        value: 'cd',
        label: 'Democratic Republic of the Congo'
      },
      {
        value: 'ck',
        label: 'Cook Islands'
      },
      {
        value: 'cr',
        label: 'Costa Rica'
      },
      {
        value: 'ci',
        label: 'Ivory Coast'
      },
      {
        value: 'hr',
        label: 'Croatia'
      },
      {
        value: 'cu',
        label: 'Cuba'
      },
      {
        value: 'cy',
        label: 'Cyprus'
      },
      {
        value: 'cz',
        label: 'Czech Republic'
      },
      {
        value: 'dk',
        label: 'Denmark'
      },
      {
        value: 'dj',
        label: 'Djibouti'
      },
      {
        value: 'dm',
        label: 'Dominica'
      },
      {
        value: 'do',
        label: 'Dominican Republic'
      },
      {
        value: 'ec',
        label: 'Ecuador'
      },
      {
        value: 'eg',
        label: 'Egypt'
      },
      {
        value: 'sv',
        label: 'El Salvador'
      },
      {
        value: 'gq',
        label: 'Equatorial Guinea'
      },
      {
        value: 'er',
        label: 'Eritrea'
      },
      {
        value: 'ee',
        label: 'Estonia'
      },
      {
        value: 'et',
        label: 'Ethiopia'
      },
      {
        value: 'fj',
        label: 'Fiji'
      },
      {
        value: 'fi',
        label: 'Finland'
      },
      {
        value: 'fr',
        label: 'France'
      },
      {
        value: 'ga',
        label: 'Gabon'
      },
      {
        value: 'gm',
        label: 'Gambia'
      },
      {
        value: 'ge',
        label: 'Georgia'
      },
      {
        value: 'de',
        label: 'Germany'
      },
      {
        value: 'gh',
        label: 'Ghana'
      },
      {
        value: 'gi',
        label: 'Gibraltar'
      },
      {
        value: 'gr',
        label: 'Greece'
      },
      {
        value: 'gl',
        label: 'Greenland'
      },
      {
        value: 'gd',
        label: 'Grenada'
      },
      {
        value: 'gu',
        label: 'Guam'
      },
      {
        value: 'gt',
        label: 'Guatemala'
      },
      {
        value: 'gn',
        label: 'Guinea'
      },
      {
        value: 'gw',
        label: 'Guinea-Bissau'
      },
      {
        value: 'gy',
        label: 'Guyana'
      },
      {
        value: 'ht',
        label: 'Haiti'
      },
      {
        value: 'hn',
        label: 'Honduras'
      },
      {
        value: 'hk',
        label: 'Hong Kong'
      },
      {
        value: 'hu',
        label: 'Hungary'
      },
      {
        value: 'is',
        label: 'Iceland'
      },
      {
        value: 'in',
        label: 'India'
      },
      {
        value: 'id',
        label: 'Indonesia'
      },
      {
        value: 'ir',
        label: 'Iran'
      },
      {
        value: 'iq',
        label: 'Iraq'
      },
      {
        value: 'ie',
        label: 'Ireland'
      },
      {
        value: 'il',
        label: 'Israel'
      },
      {
        value: 'it',
        label: 'Italy'
      },
      {
        value: 'jm',
        label: 'Jamaica'
      },
      {
        value: 'jp',
        label: 'Japan'
      },
      {
        value: 'je',
        label: 'Jersey'
      },
      {
        value: 'jo',
        label: 'Jordan'
      },
      {
        value: 'kz',
        label: 'Kazakhstan'
      },
      {
        value: 'ke',
        label: 'Kenya'
      },
      {
        value: 'ki',
        label: 'Kiribati'
      },
      {
        value: 'kp',
        label: 'D.P.R.Korea'
      },
      {
        value: 'kr',
        label: 'Republic of Korea'
      },
      {
        value: 'kw',
        label: 'Kuwait'
      },
      {
        value: 'kg',
        label: 'Kyrgyzstan'
      },
      {
        value: 'la',
        label: 'Laos'
      },
      {
        value: 'lv',
        label: 'Latvia'
      },
      {
        value: 'lb',
        label: 'Lebanon'
      },
      {
        value: 'ls',
        label: 'Lesotho'
      },
      {
        value: 'lr',
        label: 'Liberia'
      },
      {
        value: 'ly',
        label: 'Libya'
      },
      {
        value: 'li',
        label: 'Liechtenstein'
      },
      {
        value: 'lt',
        label: 'Lithuania'
      },
      {
        value: 'lu',
        label: 'Luxembourg'
      },
      {
        value: 'mo',
        label: 'Macao'
      },
      {
        value: 'mk',
        label: 'Former Yugoslav Republic of Macedonia'
      },
      {
        value: 'mg',
        label: 'Madagascar'
      },
      {
        value: 'mw',
        label: 'Malawi'
      },
      {
        value: 'my',
        label: 'Malaysia'
      },
      {
        value: 'mv',
        label: 'Maldives'
      },
      {
        value: 'ml',
        label: 'Mali'
      },
      {
        value: 'mt',
        label: 'Malta'
      },
      {
        value: 'mh',
        label: 'Marshall Islands'
      },
      {
        value: 'mr',
        label: 'Mauritania'
      },
      {
        value: 'mu',
        label: 'Mauritius'
      },
      {
        value: 'mx',
        label: 'Mexico'
      },
      {
        value: 'fm',
        label: 'Federated States of Micronesia'
      },
      {
        value: 'md',
        label: 'Moldova'
      },
      {
        value: 'mc',
        label: 'Monaco'
      },
      {
        value: 'mn',
        label: 'Mongolia'
      },
      {
        value: 'me',
        label: 'Montenegro'
      },
      {
        value: 'ma',
        label: 'Morocco'
      },
      {
        value: 'mz',
        label: 'Mozambique'
      },
      {
        value: 'mm',
        label: 'Myanmar'
      },
      {
        value: 'na',
        label: 'Namibia'
      },
      {
        value: 'nr',
        label: 'Nauru'
      },
      {
        value: 'np',
        label: 'Nepal'
      },
      {
        value: 'nl',
        label: 'Netherlands'
      },
      {
        value: 'nz',
        label: 'New Zealand'
      },
      {
        value: 'ni',
        label: 'Nicaragua'
      },
      {
        value: 'ne',
        label: 'Niger'
      },
      {
        value: 'ng',
        label: 'Nigeria'
      },
      {
        value: 'nu',
        label: 'Niue'
      },
      {
        value: 'no',
        label: 'Norway'
      },
      {
        value: 'om',
        label: 'Oman'
      },
      {
        value: 'pk',
        label: 'Pakistan'
      },
      {
        value: 'pw',
        label: 'Palau'
      },
      {
        value: 'ps',
        label: 'Palestine'
      },
      {
        value: 'pa',
        label: 'Panama'
      },
      {
        value: 'pg',
        label: 'Papua New Guinea'
      },
      {
        value: 'py',
        label: 'Paraguay'
      },
      {
        value: 'pe',
        label: 'Peru'
      },
      {
        value: 'ph',
        label: 'Philippines'
      },
      {
        value: 'pl',
        label: 'Poland'
      },
      {
        value: 'pt',
        label: 'Portugal'
      },
      {
        value: 'pr',
        label: 'Puerto Rico'
      },
      {
        value: 'qa',
        label: 'Qatar'
      },
      {
        value: 'ro',
        label: 'Romania'
      },
      {
        value: 'ru',
        label: 'Russia'
      },
      {
        value: 'rw',
        label: 'Rwanda'
      },
      {
        value: 'kn',
        label: 'Saint Kitts and Nevis'
      },
      {
        value: 'lc',
        label: 'Saint Lucia'
      },
      {
        value: 'vc',
        label: 'Saint Vincent and the Grenadines'
      },
      {
        value: 'ws',
        label: 'Samoa'
      },
      {
        value: 'sm',
        label: 'San Marino'
      },
      {
        value: 'st',
        label: 'Sao Tome and Principe'
      },
      {
        value: 'sa',
        label: 'Saudi Arabia'
      },
      {
        value: 'sn',
        label: 'Senegal'
      },
      {
        value: 'rs',
        label: 'Serbia'
      },
      {
        value: 'sc',
        label: 'Seychelles'
      },
      {
        value: 'sl',
        label: 'Sierra Leone'
      },
      {
        value: 'sg',
        label: 'Singapore'
      },
      {
        value: 'sk',
        label: 'Slovakia'
      },
      {
        value: 'si',
        label: 'Slovenia'
      },
      {
        value: 'sb',
        label: 'Solomon Islands'
      },
      {
        value: 'so',
        label: 'Somalia'
      },
      {
        value: 'za',
        label: 'South Africa'
      },
      {
        value: 'ss',
        label: 'South Sudan'
      },
      {
        value: 'es',
        label: 'Spain'
      },
      {
        value: 'lk',
        label: 'Sri Lanka'
      },
      {
        value: 'sd',
        label: 'Sudan'
      },
      {
        value: 'sr',
        label: 'Suriname'
      },
      {
        value: 'sz',
        label: 'Swaziland'
      },
      {
        value: 'se',
        label: 'Sweden'
      },
      {
        value: 'ch',
        label: 'Switzerland'
      },
      {
        value: 'sy',
        label: 'Syria'
      },
      {
        value: 'tw',
        label: 'Taiwan'
      },
      {
        value: 'tj',
        label: 'Tajikistan'
      },
      {
        value: 'tz',
        label: 'Tanzania'
      },
      {
        value: 'th',
        label: 'Thailand'
      },
      {
        value: 'tl',
        label: 'Timor-Leste'
      },
      {
        value: 'tg',
        label: 'Togo'
      },
      {
        value: 'to',
        label: 'Tonga'
      },
      {
        value: 'tt',
        label: 'Trinidad and Tobago'
      },
      {
        value: 'tn',
        label: 'Tunisia'
      },
      {
        value: 'tr',
        label: 'Turkey'
      },
      {
        value: 'tm',
        label: 'Turkmenistan'
      },
      {
        value: 'tv',
        label: 'Tuvalu'
      },
      {
        value: 'ug',
        label: 'Uganda'
      },
      {
        value: 'ua',
        label: 'Ukraine'
      },
      {
        value: 'ae',
        label: 'United Arab Emirates'
      },
      {
        value: 'gb',
        label: 'United Kingdom'
      },
      {
        value: 'us',
        label: 'United States of America'
      },
      {
        value: 'uy',
        label: 'Uruguay'
      },
      {
        value: 'uz',
        label: 'Uzbekistan'
      },
      {
        value: 'vu',
        label: 'Vanuatu'
      },
      {
        value: 'va',
        label: 'Vatican City'
      },
      {
        value: 've',
        label: 'Venezuela'
      },
      {
        value: 'vn',
        label: 'Viet Nam'
      },
      {
        value: 'ye',
        label: 'Yemen'
      },
      {
        value: 'zm',
        label: 'Zambia'
      },
      {
        value: 'zw',
        label: 'Zimbabwe'
      }
    ]
  }
}
